import { useEffect, useState } from 'react';
import { intakes, protectedAreas } from '@/api/endpoints';
import type {
  EnumOption, IntakeOperatInput, ProtectedAreaInput,
} from '@/api/endpoints';
import type {
  Intake, IntakeOperatData, ProtectedAreaReference, ProtectedAreaType, WaterUsePurpose,
} from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

interface Props {
  intake: Intake;
  onChanged: () => void;
}

/**
 * Sekcja "Dane do operatu wodnoprawnego" (Faza 3). Trzymana składana — domyślnie ukryta
 * dla użytkowników, którzy nie generują operatów. Po rozwinięciu pozwala uzupełnić JCWPd,
 * zasoby, cel korzystania, poprzednie pozwolenie, R (Sichardt/manual), GZWP i listę form
 * ochrony przyrody. Wartości wpadają do AiService.appendStructuredData.
 */
export function IntakeOperatCard({ intake, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  return (
    <div className="card">
      <div className="card-head">
        <button type="button" onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2 flex-1 text-left">
          <Icon name={open ? 'chevron' : 'chevron'}
                className={'w-3.5 h-3.5 transition-transform ' + (open ? 'rotate-90' : '')}/>
          <div className="card-title">Dane do operatu wodnoprawnego</div>
        </button>
        {open && !editing && (
          <button type="button" onClick={() => setEditing(true)}
                  className="btn btn-secondary btn-sm">
            <Icon name="edit" className="w-3.5 h-3.5"/> Edytuj
          </button>
        )}
      </div>
      {open && (
        <div className="card-body">
          {!editing && <OperatView operat={intake.operat ?? {}}/>}
          {editing && (
            <OperatEditor
              intake={intake}
              onCancel={() => setEditing(false)}
              onSaved={() => { setEditing(false); onChanged(); }}
            />
          )}
          <hr className="my-4 border-ink-100"/>
          <StateIntegrationsSection intake={intake} onChanged={onChanged}/>
          <hr className="my-4 border-ink-100"/>
          <RadiusSection intake={intake} onChanged={onChanged}/>
          <hr className="my-4 border-ink-100"/>
          <ProtectedAreasSection intakeId={intake.id} intake={intake} onChanged={onChanged}/>
        </div>
      )}
    </div>
  );
}

// ============================== View ==============================

function OperatView({ operat }: { operat: IntakeOperatData }) {
  const anyFilled = Object.values(operat).some(v => v !== null && v !== undefined && v !== '');
  if (!anyFilled) {
    return (
      <div className="text-ink-400 italic text-[12px]">
        Brak danych do operatu. Kliknij <strong>Edytuj</strong>, by uzupełnić JCWPd, zasoby,
        cel korzystania, GZWP, itp. — AI użyje tych wartości w promptach zamiast je wymyślać.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-ink-700">
      <KV k="JCWPd" v={operat.jcwpdCode ? `${operat.jcwpdCode} ${operat.jcwpdName ?? ''}` : null}/>
      <KV k="Region wodny" v={operat.regionWodny}/>
      <KV k="Dorzecze" v={operat.dorzecze}/>
      <KV k="Cel korzystania" v={operat.waterUsePurposeName}/>
      <KV k="Odbiorcy" v={operat.waterRecipients} wide/>
      <KV k="Okres pozwolenia" v={operat.requestedPermitYears != null ? `${operat.requestedPermitYears} lat` : null}/>
      <KV k="Zatwierdzone zasoby"
          v={operat.approvedResourceM3h != null
              ? `${operat.approvedResourceM3h} m³/h${operat.approvedResourceDecisionRef ? ' (' + operat.approvedResourceDecisionRef + ')' : ''}`
              : null}/>
      <KV k="Poprzednie pozwolenie"
          v={operat.previousPermitRef
              ? `${operat.previousPermitRef}${operat.previousPermitExpiry ? ', wygasa ' + operat.previousPermitExpiry : ''}`
              : null}/>
      <KV k="Uzdatnianie" v={operat.treatmentDescription} wide/>
      <KV k="Dystrybucja" v={operat.distributionDescription} wide/>
      <KV k="GZWP" v={operat.inGzwp ? `nr ${operat.gzwpNumber ?? '?'}${operat.gzwpName ? ' "' + operat.gzwpName + '"' : ''}` : (operat.inGzwp === false ? 'poza GZWP' : null)}/>
      <KV k="Strefa zagrożenia powodzią" v={operat.inFloodHazardArea === true ? 'TAK' : operat.inFloodHazardArea === false ? 'NIE' : null}/>
    </div>
  );
}

function KV({ k, v, wide }: { k: string; v?: string | number | null; wide?: boolean }) {
  if (v === null || v === undefined || v === '') return null;
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <span className="text-ink-400">{k}:</span> <span className="text-ink-800">{v}</span>
    </div>
  );
}

// ============================== Editor ==============================

function OperatEditor({ intake, onCancel, onSaved }: { intake: Intake; onCancel: () => void; onSaved: () => void }) {
  const o = intake.operat ?? {};
  const [form, setForm] = useState<IntakeOperatInput>({
    jcwpdCode: o.jcwpdCode ?? null,
    jcwpdName: o.jcwpdName ?? null,
    regionWodny: o.regionWodny ?? null,
    dorzecze: o.dorzecze ?? null,
    waterUsePurpose: (o.waterUsePurpose ?? null) as WaterUsePurpose | null,
    waterUseDescription: o.waterUseDescription ?? null,
    waterRecipients: o.waterRecipients ?? null,
    requestedPermitYears: o.requestedPermitYears ?? null,
    approvedResourceM3h: o.approvedResourceM3h ?? null,
    approvedResourceDecisionRef: o.approvedResourceDecisionRef ?? null,
    approvedResourceDecisionDate: o.approvedResourceDecisionDate ?? null,
    approvedResourceDecisionAuthority: o.approvedResourceDecisionAuthority ?? null,
    previousPermitRef: o.previousPermitRef ?? null,
    previousPermitDate: o.previousPermitDate ?? null,
    previousPermitAuthority: o.previousPermitAuthority ?? null,
    previousPermitExpiry: o.previousPermitExpiry ?? null,
    treatmentDescription: o.treatmentDescription ?? null,
    distributionDescription: o.distributionDescription ?? null,
    inFloodHazardArea: o.inFloodHazardArea ?? null,
    inGzwp: o.inGzwp ?? null,
    gzwpNumber: o.gzwpNumber ?? null,
    gzwpName: o.gzwpName ?? null,
  });
  const [purposes, setPurposes] = useState<EnumOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { intakes.waterUsePurposes().then(setPurposes).catch(() => { /* nonfatal */ }); }, []);

  const set = <K extends keyof IntakeOperatInput>(k: K, v: IntakeOperatInput[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await intakes.updateOperat(intake.id, form);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 text-[12px]">
      <FieldSection title="Hydrografia (JCWPd, region, dorzecze)">
        <div className="grid grid-cols-2 gap-2">
          <Input label="Kod JCWPd" value={form.jcwpdCode ?? ''}
                 onChange={v => set('jcwpdCode', v || null)}
                 placeholder="np. PLGW600060"/>
          <Input label="Nazwa JCWPd" value={form.jcwpdName ?? ''}
                 onChange={v => set('jcwpdName', v || null)}
                 placeholder="np. 60 (Pradolina Barycko-Głogowska)"/>
          <Input label="Region wodny" value={form.regionWodny ?? ''}
                 onChange={v => set('regionWodny', v || null)}
                 placeholder="np. Warty"/>
          <Input label="Dorzecze" value={form.dorzecze ?? ''}
                 onChange={v => set('dorzecze', v || null)}
                 placeholder="np. Odry"/>
        </div>
      </FieldSection>

      <FieldSection title="Cel i zakres korzystania z wód">
        <div className="grid grid-cols-2 gap-2">
          <Select label="Cel" value={form.waterUsePurpose ?? ''}
                  onChange={v => set('waterUsePurpose', (v || null) as WaterUsePurpose | null)}
                  options={[{ value: '', label: '— nie wybrano —' },
                            ...purposes.map(p => ({ value: p.code, label: p.displayName }))]}/>
          <Input label="Okres pozwolenia [lat]" value={form.requestedPermitYears?.toString() ?? ''}
                 onChange={v => set('requestedPermitYears', v ? Number.parseInt(v, 10) : null)}
                 placeholder="30" inputMode="numeric"/>
          <Textarea label="Opis celu (jeśli wybrano Inne)" value={form.waterUseDescription ?? ''}
                    onChange={v => set('waterUseDescription', v || null)}
                    wide/>
          <Textarea label="Odbiorcy wody" value={form.waterRecipients ?? ''}
                    onChange={v => set('waterRecipients', v || null)}
                    placeholder="np. mieszkańcy gminy Pakosław (3 500 osób)"
                    wide/>
        </div>
      </FieldSection>

      <FieldSection title="Zatwierdzone zasoby eksploatacyjne (z decyzji)">
        <div className="grid grid-cols-2 gap-2">
          <Input label="Zasoby [m³/h]" value={form.approvedResourceM3h?.toString() ?? ''}
                 onChange={v => set('approvedResourceM3h', v ? Number(v) : null)}
                 inputMode="decimal"/>
          <Input label="Nr decyzji" value={form.approvedResourceDecisionRef ?? ''}
                 onChange={v => set('approvedResourceDecisionRef', v || null)}/>
          <Input label="Data decyzji" type="date" value={form.approvedResourceDecisionDate ?? ''}
                 onChange={v => set('approvedResourceDecisionDate', v || null)}/>
          <Input label="Organ wydający" value={form.approvedResourceDecisionAuthority ?? ''}
                 onChange={v => set('approvedResourceDecisionAuthority', v || null)}
                 placeholder="np. Marszałek Województwa Wielkopolskiego"/>
        </div>
      </FieldSection>

      <FieldSection title="Poprzednie pozwolenie wodnoprawne">
        <div className="grid grid-cols-2 gap-2">
          <Input label="Nr pozwolenia" value={form.previousPermitRef ?? ''}
                 onChange={v => set('previousPermitRef', v || null)}/>
          <Input label="Data wydania" type="date" value={form.previousPermitDate ?? ''}
                 onChange={v => set('previousPermitDate', v || null)}/>
          <Input label="Organ wydający" value={form.previousPermitAuthority ?? ''}
                 onChange={v => set('previousPermitAuthority', v || null)}/>
          <Input label="Data wygaśnięcia" type="date" value={form.previousPermitExpiry ?? ''}
                 onChange={v => set('previousPermitExpiry', v || null)}/>
        </div>
      </FieldSection>

      <FieldSection title="Uzdatnianie i dystrybucja">
        <div className="grid grid-cols-2 gap-2">
          <Textarea label="Stacja uzdatniania (SUW)" value={form.treatmentDescription ?? ''}
                    onChange={v => set('treatmentDescription', v || null)} wide/>
          <Textarea label="Sieć dystrybucji" value={form.distributionDescription ?? ''}
                    onChange={v => set('distributionDescription', v || null)} wide/>
        </div>
      </FieldSection>

      <FieldSection title="GZWP i strefy zagrożeń">
        <div className="grid grid-cols-2 gap-2">
          <BoolSelect label="W obszarze GZWP" value={form.inGzwp ?? null}
                      onChange={v => set('inGzwp', v)}/>
          <Input label="Numer GZWP" value={form.gzwpNumber?.toString() ?? ''}
                 onChange={v => set('gzwpNumber', v ? Number.parseInt(v, 10) : null)}
                 inputMode="numeric"/>
          <Input label="Nazwa GZWP" value={form.gzwpName ?? ''}
                 onChange={v => set('gzwpName', v || null)} wide/>
          <BoolSelect label="W strefie zagrożenia powodzią" value={form.inFloodHazardArea ?? null}
                      onChange={v => set('inFloodHazardArea', v)}/>
        </div>
      </FieldSection>

      {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}

      <div className="flex gap-2 pt-1">
        <button type="button" onClick={save} disabled={busy} className="btn btn-primary btn-sm">
          <Icon name="check" className="w-3.5 h-3.5"/> Zapisz
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className="btn btn-secondary btn-sm">
          Anuluj
        </button>
      </div>
    </div>
  );
}

function FieldSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wide mb-1">{title}</div>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder, inputMode, wide }: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; inputMode?: 'numeric' | 'decimal' | 'text'; wide?: boolean;
}) {
  return (
    <label className={'block ' + (wide ? 'col-span-2' : '')}>
      <span className="text-[11px] text-ink-500">{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
             placeholder={placeholder} inputMode={inputMode}
             className="input w-full"/>
    </label>
  );
}

function Textarea({ label, value, onChange, placeholder, wide }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; wide?: boolean;
}) {
  return (
    <label className={'block ' + (wide ? 'col-span-2' : '')}>
      <span className="text-[11px] text-ink-500">{label}</span>
      <textarea value={value} onChange={e => onChange(e.target.value)}
                placeholder={placeholder} rows={2}
                className="input w-full text-[12px]"/>
    </label>
  );
}

function Select({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="block">
      <span className="text-[11px] text-ink-500">{label}</span>
      <SearchableSelect value={value} onChange={onChange} className="w-full">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </SearchableSelect>
    </label>
  );
}

function BoolSelect({ label, value, onChange }: {
  label: string; value: boolean | null; onChange: (v: boolean | null) => void;
}) {
  const s = value === null ? '' : value ? 'true' : 'false';
  return (
    <label className="block">
      <span className="text-[11px] text-ink-500">{label}</span>
      <SearchableSelect value={s}
              onChange={v => onChange(v === '' ? null : v === 'true')}
              className="w-full">
        <option value="">— nie wskazano —</option>
        <option value="true">TAK</option>
        <option value="false">NIE</option>
      </SearchableSelect>
    </label>
  );
}

// ============================== Radius (R) ==============================

function RadiusSection({ intake, onChanged }: { intake: Intake; onChanged: () => void }) {
  const op = intake.operat ?? {};
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualValue, setManualValue] = useState<string>(op.radiusM?.toString() ?? '');
  const [manualSource, setManualSource] = useState<'MANUAL' | 'DOKUMENTACJA'>('MANUAL');
  const [manualComment, setManualComment] = useState('');

  const calcSichardt = async () => {
    setBusy(true); setErr(null); setInfo(null);
    try {
      const r = await intakes.calcSichardt(intake.id);
      setInfo(`Obliczono: R = ${r.radiusM} m (s_max=${r.maxDrawdownM} m, k_śr=${r.avgPermeabilityKMs} m/s, ${r.boreholesUsed} studni).`);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const saveManual = async () => {
    const n = Number(manualValue);
    if (!Number.isFinite(n) || n <= 0) { setErr('Wartość R musi być dodatnią liczbą.'); return; }
    setBusy(true); setErr(null); setInfo(null);
    try {
      await intakes.setRadiusManual(intake.id, n, manualSource, manualComment || undefined);
      setInfo(`Zapisano R = ${n} m (metoda: ${manualSource}).`);
      setManualOpen(false);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wide mb-1">
        Zasięg oddziaływania (R)
      </div>
      <div className="flex items-baseline gap-3 text-[12px]">
        <div>
          <span className="text-ink-400">R:</span>{' '}
          <span className="font-mono text-ink-800">{op.radiusM != null ? `${op.radiusM} m` : '— brak —'}</span>
          {op.radiusMethod && <span className="text-ink-400 ml-1">({op.radiusMethod})</span>}
        </div>
        <button type="button" onClick={calcSichardt} disabled={busy}
                className="btn btn-secondary btn-sm" title="Wzór Sichardta: R = 575·s·√k">
          <Icon name="check" className="w-3.5 h-3.5"/> Oblicz Sichardt
        </button>
        <button type="button" onClick={() => setManualOpen(o => !o)} disabled={busy}
                className="btn btn-secondary btn-sm">
          <Icon name="edit" className="w-3.5 h-3.5"/> Wprowadź ręcznie
        </button>
      </div>
      {manualOpen && (
        <div className="mt-2 grid grid-cols-3 gap-2 text-[12px]">
          <Input label="R [m]" value={manualValue} onChange={setManualValue} inputMode="decimal"/>
          <Select label="Źródło" value={manualSource}
                  onChange={v => setManualSource(v as 'MANUAL' | 'DOKUMENTACJA')}
                  options={[{ value: 'MANUAL', label: 'Manual' }, { value: 'DOKUMENTACJA', label: 'Dokumentacja hydrogeologiczna' }]}/>
          <Input label="Komentarz" value={manualComment} onChange={setManualComment}/>
          <button type="button" onClick={saveManual} disabled={busy}
                  className="btn btn-primary btn-sm col-span-3 max-w-fit">
            <Icon name="check" className="w-3.5 h-3.5"/> Zapisz
          </button>
        </div>
      )}
      {info && <div className="mt-2 text-[11px] text-ink-700 bg-ink-50 border border-ink-100 rounded p-2">{info}</div>}
      {err && <div className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
    </div>
  );
}

// ============================== R&D F-INT — integracje państwowe ==============================

function StateIntegrationsSection({ intake, onChanged }: { intake: Intake; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const hasCoords = intake.latitude != null && intake.longitude != null;

  const runJcwpd = async () => {
    setBusy('jcwpd'); setMsg(null);
    try {
      const r = await intakes.jcwpdLookup(intake.id);
      if (r.ok) {
        setMsg({ kind: 'ok',
          text: `JCWPd: ${r.jcwpdCode} (${r.jcwpdName}), region ${r.regionWodny}, dorzecze ${r.dorzecze}${r.fromCache ? ' [cache]' : ''}` });
        onChanged();
      } else {
        setMsg({ kind: 'err', text: r.reason ?? 'Brak danych z PGW WP.' });
      }
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(null); }
  };

  const runFlood = async () => {
    setBusy('flood'); setMsg(null);
    try {
      const r = await intakes.floodCheck(intake.id);
      if (r.ok) {
        const parts = [];
        if (r.q10) parts.push('Q10%');
        if (r.q1) parts.push('Q1%');
        if (r.q02) parts.push('Q0.2%');
        setMsg({ kind: 'ok',
          text: r.inFloodHazardArea
            ? `Zagrożenie powodziowe: TAK (${parts.join(', ')})${r.fromCache ? ' [cache]' : ''}`
            : `Poza obszarem zagrożenia powodziowego${r.fromCache ? ' [cache]' : ''}` });
        onChanged();
      } else {
        setMsg({ kind: 'err', text: r.reason ?? 'Brak danych z ISOK MZP.' });
      }
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wide mb-2">
        Pobieranie danych z systemów państwowych
      </div>
      {!hasCoords && (
        <div className="text-ink-400 italic text-[12px] mb-2">
          Brak współrzędnych lat/lon — uzupełnij lokalizację ujęcia, by skorzystać z integracji.
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-2">
        <button type="button"
                disabled={!hasCoords || busy !== null}
                onClick={runJcwpd}
                className="btn btn-secondary btn-sm"
                title="Pobiera kod JCWPd, region wodny i dorzecze z hydroportalu PGW Wody Polskie">
          <Icon name="search" className="w-3.5 h-3.5"/>
          {busy === 'jcwpd' ? 'Pobieram…' : 'JCWPd z hydroportalu'}
        </button>
        <button type="button"
                disabled={!hasCoords || busy !== null}
                onClick={runFlood}
                className="btn btn-secondary btn-sm"
                title="Sprawdza czy ujęcie leży w obszarach Q10%/Q1%/Q0.2% (Mapy Zagrożenia Powodziowego ISOK)">
          <Icon name="search" className="w-3.5 h-3.5"/>
          {busy === 'flood' ? 'Sprawdzam…' : 'Sprawdź MZP ISOK'}
        </button>
      </div>
      {msg && (
        <div className={'text-[12px] ' + (msg.kind === 'ok' ? 'text-emerald-700' : 'text-rose-700')}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

// ============================== Protected areas ==============================

function ProtectedAreasSection({ intakeId, intake, onChanged }: {
  intakeId: number;
  intake: Intake;
  onChanged: () => void;
}) {
  const [list, setList] = useState<ProtectedAreaReference[]>([]);
  const [types, setTypes] = useState<EnumOption[]>([]);
  const [editing, setEditing] = useState<ProtectedAreaReference | null>(null);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => {
    protectedAreas.list(intakeId).then(setList).catch(e => setErr(String(e)));
  };

  useEffect(() => {
    reload();
    protectedAreas.types().then(setTypes).catch(() => { /* nonfatal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeId]);

  const remove = async (id: number) => {
    if (!confirm('Usunąć formę ochrony?')) return;
    try {
      await protectedAreas.remove(id);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const [autoDetectBusy, setAutoDetectBusy] = useState(false);
  const [autoDetectMsg, setAutoDetectMsg] = useState<string | null>(null);
  const hasCoords = intake.latitude != null && intake.longitude != null;
  const runAutoDetect = async () => {
    if (!confirm('Auto-wykrywanie z GDOŚ Geoserwis zaktualizuje wpisy auto-detect (manualne wpisy zostają nienaruszone). Kontynuować?')) return;
    setAutoDetectBusy(true); setAutoDetectMsg(null); setErr(null);
    try {
      const r = await intakes.autoDetectProtectedAreas(intakeId);
      setAutoDetectMsg(`GDOŚ: ${r.foundTotal} form, utw. ${r.created}, akt. ${r.updated}, manualne pominięte ${r.keptManual}.`);
      reload();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setAutoDetectBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center mb-2 gap-2">
        <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wide">
          Formy ochrony przyrody w zasięgu
        </div>
        <button type="button"
                onClick={runAutoDetect}
                disabled={!hasCoords || autoDetectBusy}
                className="ml-auto btn btn-secondary btn-sm"
                title="Auto-wykrywanie z GDOŚ Geoserwis w promieniu R wokół ujęcia.">
          <Icon name="search" className="w-3.5 h-3.5"/>
          {autoDetectBusy ? 'Wykrywam…' : 'Auto-wykryj (GDOŚ)'}
        </button>
      </div>
      {autoDetectMsg && (
        <div className="text-[12px] text-emerald-700 mb-2">{autoDetectMsg}</div>
      )}
      {list.length === 0 && !adding && (
        <div className="text-ink-400 italic text-[12px]">
          Brak. Po dodaniu — AI w sekcji „Formy ochrony przyrody" użyje tej listy.
          Bez wpisu prompt informuje: „BRAK form ochrony w zasięgu oddziaływania".
        </div>
      )}
      {list.length > 0 && (
        <table className="table-hw text-[11px]">
          <thead><tr><th>Typ</th><th>Nazwa</th><th>Kod</th><th>Odl. [m]</th><th></th></tr></thead>
          <tbody>
            {list.map(a => (
              <tr key={a.id} className={editing?.id === a.id ? 'bg-ink-50' : ''}>
                <td>{a.typeDisplayName}</td>
                <td>{a.name}</td>
                <td className="font-mono">{a.code ?? '—'}</td>
                <td className="font-mono tabular-nums">{a.distanceM ?? '—'}</td>
                <td className="text-right whitespace-nowrap">
                  <button type="button" onClick={() => { setEditing(a); setAdding(false); }}
                          className="p-1 text-ink-400 hover:text-water-600" title="Edytuj">
                    <Icon name="edit" className="w-3 h-3"/>
                  </button>
                  <button type="button" onClick={() => remove(a.id)}
                          className="p-1 text-ink-400 hover:text-red-600" title="Usuń">
                    <Icon name="x" className="w-3 h-3"/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <button type="button" onClick={() => { setAdding(true); setEditing(null); }}
              className="btn btn-secondary btn-sm mt-2">
        <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj
      </button>
      {(adding || editing) && (
        <ProtectedAreaForm
          intakeId={intakeId}
          types={types}
          existing={editing}
          onCancel={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); reload(); }}
        />
      )}
      {err && <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2">{err}</div>}
    </div>
  );
}

function ProtectedAreaForm({ intakeId, types, existing, onCancel, onSaved }: {
  intakeId: number; types: EnumOption[];
  existing: ProtectedAreaReference | null;
  onCancel: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<ProtectedAreaInput>({
    type: (existing?.type ?? 'NATURA_2000_OSO') as ProtectedAreaType,
    name: existing?.name ?? '',
    code: existing?.code ?? '',
    distanceM: existing?.distanceM ?? null,
    objectOfProtection: existing?.objectOfProtection ?? '',
    impactAssessment: existing?.impactAssessment ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      if (existing) await protectedAreas.update(existing.id, form);
      else await protectedAreas.add(intakeId, form);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 p-3 bg-ink-50 border border-ink-100 rounded space-y-2 text-[12px]">
      <div className="grid grid-cols-2 gap-2">
        <Select label="Typ" value={form.type}
                onChange={v => setForm({ ...form, type: v as ProtectedAreaType })}
                options={types.map(t => ({ value: t.code, label: t.displayName }))}/>
        <Input label="Nazwa *" value={form.name} onChange={v => setForm({ ...form, name: v })}/>
        <Input label="Kod" value={form.code ?? ''} onChange={v => setForm({ ...form, code: v || null })}
               placeholder="np. PLB300013"/>
        <Input label="Odległość [m]" value={form.distanceM?.toString() ?? ''}
               onChange={v => setForm({ ...form, distanceM: v ? Number(v) : null })}
               inputMode="decimal"/>
        <Textarea label="Przedmiot ochrony" value={form.objectOfProtection ?? ''}
                  onChange={v => setForm({ ...form, objectOfProtection: v || null })} wide/>
        <Textarea label="Ocena wpływu eksploatacji" value={form.impactAssessment ?? ''}
                  onChange={v => setForm({ ...form, impactAssessment: v || null })} wide/>
      </div>
      {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
      <div className="flex gap-2">
        <button type="button" onClick={save} disabled={busy || !form.name.trim()}
                className="btn btn-primary btn-sm">
          <Icon name="check" className="w-3.5 h-3.5"/> Zapisz
        </button>
        <button type="button" onClick={onCancel} disabled={busy}
                className="btn btn-secondary btn-sm">Anuluj</button>
      </div>
    </div>
  );
}
